import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { salesAPI, nfceAPI, api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/Toast';
import { 
  FileText, Eye, Download, FileArchive, Trash2, Calendar, RefreshCw, 
  AlertTriangle, CheckCircle, XCircle, Loader2, Pencil, Ban, Hash,
  Monitor, MessageCircle, Globe, Receipt, Store, Search, Printer
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { EditSaleModal } from '@/components/EditSaleModal';
import { SimpleModal } from '@/components/SimpleModal';
import { getSalePaymentLabel } from '@/lib/payments';

// Ícones por fonte de venda
const sourceIcons = {
  pdv: { icon: Monitor, color: 'text-blue-600', bg: 'bg-blue-100', label: 'PDV' },
  whatsapp: { icon: MessageCircle, color: 'text-green-600', bg: 'bg-green-100', label: 'WhatsApp' },
  online: { icon: Globe, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Online' },
};

export const SalesPage = () => {
  const toast = useToast();
  const [sales, setSales] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // Novo filtro de status fiscal
  const [totals, setTotals] = useState({ pdv: 0, whatsapp: 0, online: 0, geral: 0 });
  
  // Modais separados
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showInutilizarModal, setShowInutilizarModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEmitirNotaModal, setShowEmitirNotaModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [cancelingSale, setCancelingSale] = useState(null);
  const [tipoDocumento, setTipoDocumento] = useState('nfce');
  const [emitindoNota, setEmitindoNota] = useState(false);
  
  // Estados dos formulários
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [xmlCount, setXmlCount] = useState(0);
  const [tipoDocumentoDownload, setTipoDocumentoDownload] = useState('todos'); // 'nfce', 'nfe', 'todos'
  const [cancelJustificativa, setCancelJustificativa] = useState('');
  const [inutSerie, setInutSerie] = useState(1);
  const [inutNumeroInicial, setInutNumeroInicial] = useState('');
  const [inutNumeroFinal, setInutNumeroFinal] = useState('');
  const [inutJustificativa, setInutJustificativa] = useState('');
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [reenviando, setReenviando] = useState(null);
  const [duplicados, setDuplicados] = useState(null);
  const [showDuplicadosModal, setShowDuplicadosModal] = useState(false);
  const [limpandoDuplicados, setLimpandoDuplicados] = useState(false);
  const [diagnostico, setDiagnostico] = useState(null);
  const [loadingDiagnostico, setLoadingDiagnostico] = useState(false);
  
  // Estados para notas em simulação
  const [showSimulacaoModal, setShowSimulacaoModal] = useState(false);
  const [vendasSimulacao, setVendasSimulacao] = useState([]);
  const [loadingSimulacao, setLoadingSimulacao] = useState(false);
  const [reenviadoLote, setReenviadoLote] = useState(false);
  
  // Estados para análise de números
  const [analiseNumeros, setAnaliseNumeros] = useState(null);
  const [loadingAnalise, setLoadingAnalise] = useState(false);
  const [corrigindoNumeros, setCorrigindoNumeros] = useState(false);
  const [limpandoSimulacao, setLimpandoSimulacao] = useState(false);

  useEffect(() => {
    loadSales();
    loadDiagnostico();
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setDataInicio(firstDay.toISOString().split('T')[0]);
    setDataFim(now.toISOString().split('T')[0]);
  }, []);

  // Carregar vendas em simulação
  const loadVendasSimulacao = async () => {
    try {
      setLoadingSimulacao(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/nfce/simulacao/pendentes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setVendasSimulacao(data.vendas || []);
      }
    } catch (error) {
      console.error('Erro ao carregar vendas em simulação:', error);
      toast.error('Erro ao carregar vendas em simulação');
    } finally {
      setLoadingSimulacao(false);
    }
  };

  // Reenviar todas em lote
  const handleReenviarLote = async () => {
    try {
      setReenviadoLote(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/nfce/simulacao/reenviar-lote`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Processado: ${data.sucesso} autorizadas, ${data.erro} com erro de ${data.total} total`);
        loadVendasSimulacao();
        loadSales();
      } else {
        toast.error(data.detail || 'Erro ao processar lote');
      }
    } catch (error) {
      console.error('Erro ao reenviar lote:', error);
      toast.error('Erro ao reenviar lote de NFC-e');
    } finally {
      setReenviadoLote(false);
    }
  };

  // Analisar números NFC-e
  const handleAnalisarNumeros = async () => {
    try {
      setLoadingAnalise(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/nfce/numeros/analise`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setAnaliseNumeros(data);
      } else {
        toast.error(data.detail || 'Erro ao analisar números');
      }
    } catch (error) {
      console.error('Erro ao analisar números:', error);
      toast.error('Erro ao analisar números');
    } finally {
      setLoadingAnalise(false);
    }
  };

  // Buscar vendas duplicadas
  const loadDuplicados = async () => {
    try {
      const response = await salesAPI.getDuplicados();
      setDuplicados(response.data);
    } catch (error) {
      console.error('Erro ao buscar duplicados:', error);
      toast.error('Erro ao buscar vendas duplicadas');
    }
  };

  // Limpar duplicados
  const handleLimparDuplicados = async () => {
    if (!window.confirm('Tem certeza que deseja excluir as vendas duplicadas? As vendas autorizadas serão mantidas.')) {
      return;
    }
    
    try {
      setLimpandoDuplicados(true);
      const response = await salesAPI.limparDuplicados();
      toast.success(response.data.message);
      setDuplicados(null);
      setShowDuplicadosModal(false);
      loadSales();
    } catch (error) {
      console.error('Erro ao limpar duplicados:', error);
      toast.error(error.response?.data?.detail || 'Erro ao limpar duplicados');
    } finally {
      setLimpandoDuplicados(false);
    }
  };

  // Excluir venda individual
  const handleExcluirVenda = async (saleId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta venda?')) {
      return;
    }
    
    try {
      await salesAPI.delete(saleId);
      toast.success('Venda excluída com sucesso');
      loadSales();
      loadVendasSimulacao();
    } catch (error) {
      console.error('Erro ao excluir venda:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir venda');
    }
  };



  // Carregar diagnóstico NFC-e
  const loadDiagnostico = async () => {
    try {
      setLoadingDiagnostico(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/nfce/diagnostico`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setDiagnostico(data);
      }
    } catch (error) {
      console.error('Erro ao carregar diagnóstico:', error);
    } finally {
      setLoadingDiagnostico(false);
    }
  };

  // Corrigir próximo número
  const handleCorrigirNumeros = async () => {
    try {
      setCorrigindoNumeros(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/nfce/numeros/corrigir`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.mensagem);
        handleAnalisarNumeros();
      } else {
        toast.error(data.detail || 'Erro ao corrigir números');
      }
    } catch (error) {
      console.error('Erro ao corrigir números:', error);
      toast.error('Erro ao corrigir números');
    } finally {
      setCorrigindoNumeros(false);
    }
  };

  // Limpar números de simulação
  const handleLimparSimulacao = async () => {
    if (!window.confirm('Isso vai remover os números das vendas em simulação para que possam ser reutilizados. Continuar?')) {
      return;
    }
    try {
      setLimpandoSimulacao(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/nfce/numeros/limpar-simulacao`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.mensagem);
        loadVendasSimulacao();
        handleAnalisarNumeros();
        loadSales();
      } else {
        toast.error(data.detail || 'Erro ao limpar simulação');
      }
    } catch (error) {
      console.error('Erro ao limpar simulação:', error);
      toast.error('Erro ao limpar simulação');
    } finally {
      setLimpandoSimulacao(false);
    }
  };

  // Reenviar NFC-e de simulação individual
  const handleReenviarSimulacao = async (vendaId) => {
    try {
      setReenviando(vendaId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/nfce/simulacao/reenviar/${vendaId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok && data.success && data.autorizado) {
        toast.success(`NFC-e ${data.numero} autorizada com sucesso!`);
        loadVendasSimulacao();
        loadSales();
      } else if (response.ok && data.success && data.simulado) {
        toast.warning('NFC-e ainda em simulação - verifique certificado e CSC');
      } else {
        toast.error(data.detail || data.erro || 'Erro ao reenviar NFC-e');
      }
    } catch (error) {
      console.error('Erro ao reenviar NFC-e:', error);
      toast.error('Erro ao reenviar NFC-e');
    } finally {
      setReenviando(null);
    }
  };

  const loadSales = async () => {
    try {
      setLoading(true);
      // Usar endpoint unificado
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/sales/unified?limit=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        setSales(data.sales || []);
        setTotals(data.totals || { pdv: 0, whatsapp: 0, online: 0, geral: 0 });
      } else {
        // Fallback para endpoint antigo
        const fallbackResponse = await salesAPI.getAll(100);
        setSales(fallbackResponse.data);
      }
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
      toast.error('Erro ao carregar vendas');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckXmlCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/nfce/xmls?data_inicio=${dataInicio}&data_fim=${dataFim}&tipo_documento=${tipoDocumentoDownload}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setXmlCount(data.total || 0);
    } catch (error) {
      toast.error('Erro ao verificar XMLs');
    }
  };

  const handleDownloadBatch = async () => {
    const token = localStorage.getItem('token');
    const url = `${BACKEND_URL}/api/nfce/xmls/download?data_inicio=${dataInicio}&data_fim=${dataFim}&tipo_documento=${tipoDocumentoDownload}`;
    toast.info('Preparando download...');
    try {
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error('Erro no download');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const tipoLabel = tipoDocumentoDownload === 'nfce' ? 'NFCe' : tipoDocumentoDownload === 'nfe' ? 'NFe' : 'Notas';
      link.setAttribute('download', `XMLs_${tipoLabel}_${dataInicio}_a_${dataFim}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        toast.success('Download concluído!');
        setShowDownloadModal(false);
      }, 50);
    } catch (error) {
      setTimeout(() => {
        toast.error('Erro ao baixar XMLs');
      }, 50);
    }
  };

  const handleCancelarNfce = async () => {
    if (!cancelingSale || cancelJustificativa.length < 15) {
      toast.warning('Justificativa deve ter no mínimo 15 caracteres');
      return;
    }
    
    setShowCancelModal(false);
    const saleId = cancelingSale.id;
    const justificativa = cancelJustificativa;
    setCancelingSale(null);
    setCancelJustificativa('');
    
    try {
      const response = await salesAPI.cancelarNfce(saleId, justificativa);
      loadSales();
      
      if (response.data.sucesso) {
        toast.success('NFC-e cancelada com sucesso!');
      } else {
        toast.error(response.data.erro || 'Erro ao cancelar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar NFC-e');
    }
  };

  const handleInutilizar = async () => {
    if (inutJustificativa.length < 15 || !inutNumeroInicial || !inutNumeroFinal) {
      toast.warning('Preencha todos os campos corretamente');
      return;
    }
    
    setShowInutilizarModal(false);
    
    try {
      const response = await nfceAPI.inutilizar({
        serie: parseInt(inutSerie),
        numero_inicial: parseInt(inutNumeroInicial),
        numero_final: parseInt(inutNumeroFinal),
        justificativa: inutJustificativa
      });
      
      setInutNumeroInicial('');
      setInutNumeroFinal('');
      setInutJustificativa('');
      
      if (response.data.sucesso) {
        toast.success('Numeração inutilizada com sucesso!');
      } else {
        toast.error(response.data.erro || 'Erro ao inutilizar');
      }
    } catch (error) {
      setInutNumeroInicial('');
      setInutNumeroFinal('');
      setInutJustificativa('');
      toast.error(error.response?.data?.detail || 'Erro ao inutilizar');
    }
  };

  const handleDownloadXml = async (saleId) => {
    try {
      const resp = await api.get(`/nfce/xml/${saleId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFCe_${saleId}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('XML baixado com sucesso');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro ao baixar XML';
      toast.error(`Falha ao baixar XML: ${msg}`);
    }
  };

  const handleReprintCupom = async (saleId) => {
    try {
      // Carregar dados completos da venda + dados da empresa em paralelo
      const [saleResp, companyResp] = await Promise.all([
        api.get(`/sales/${saleId}`),
        api.get('/settings/company').catch(() => ({ data: {} })),
      ]);
      const sale = saleResp.data || {};
      const company = companyResp.data || {};

      const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      const safe = (v) => (v == null ? '' : String(v).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])));
      const items = sale.items || sale.itens || [];
      const itemsHtml = items.map(it => {
        const name = it.product_name || it.name || it.nome || '';
        const qty = Number(it.quantity || it.qtd || it.qty || 1);
        const price = Number(it.unit_price || it.preco_unitario || it.price || 0);
        const total = Number(it.total || it.subtotal || (qty * price));
        return `<div class="row">
          <div class="col-name">${safe(name)}</div>
          <div class="col-qty">${qty.toLocaleString('pt-BR', { maximumFractionDigits: 3 })} × ${brl(price)}</div>
          <div class="col-tot">${brl(total)}</div>
        </div>`;
      }).join('');

      const totalSale = Number(
        sale.total ?? sale.valor_total ?? items.reduce((s, it) => s + Number(it.total || it.subtotal || 0), 0)
      );
      const paymentMethod = getSalePaymentLabel(sale);
      const customerName = sale.customer_name || sale.cliente_nome || '';
      const customerCpf = sale.customer_cpf || sale.cliente_cpf || '';
      const isFiscal = !!(sale.nfce_key && !sale.nfce_simulado);
      const dateStr = sale.created_at ? new Date(sale.created_at).toLocaleString('pt-BR') : new Date().toLocaleString('pt-BR');

      const headerLines = [
        (company.razao_social || company.nome_fantasia || 'EMPRESA').toUpperCase(),
        company.nome_fantasia && company.razao_social && company.nome_fantasia !== company.razao_social ? company.nome_fantasia : '',
        company.cnpj ? `CNPJ: ${company.cnpj}` : '',
        (company.inscricao_estadual || company.ie) ? `IE: ${company.inscricao_estadual || company.ie}` : '',
        [
          company.endereco || company.logradouro,
          company.numero ? `nº ${company.numero}` : '',
          company.bairro ? `- ${company.bairro}` : '',
        ].filter(Boolean).join(' '),
        [company.cidade, company.uf].filter(Boolean).join(' - ') + (company.cep ? ` CEP ${company.cep}` : ''),
        company.telefone ? `Tel: ${company.telefone}` : '',
      ].filter(s => s && s.trim()).map(safe).join('<br/>');

      const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<title>${isFiscal ? 'NFC-e' : 'Comprovante'} ${sale.nfce_number || ''}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; font-size: 11px; color: #000; margin: 0; padding: 8mm; }
  .ticket { width: 80mm; max-width: 80mm; margin: 0 auto; }
  .center { text-align: center; }
  .bold { font-weight: 700; }
  .sm { font-size: 10px; }
  .divider { border-top: 1px dashed #000; margin: 6px 0; }
  .header { text-align: center; }
  .row { display: flex; justify-content: space-between; gap: 4px; }
  .col-name { flex: 1 1 auto; }
  .col-qty { white-space: nowrap; }
  .col-tot { white-space: nowrap; min-width: 60px; text-align: right; }
  .item { margin-bottom: 3px; }
  .item .row + .row { font-size: 10px; color: #333; }
  .totals .row { font-weight: 700; }
  .key { word-break: break-all; font-size: 9px; text-align: center; }
  .reimpressao { display: inline-block; padding: 2px 6px; border: 1px dashed #000; margin-top: 4px; font-size: 9px; }
  @media print {
    body { padding: 0; }
    .no-print { display: none !important; }
    @page { size: 80mm auto; margin: 4mm; }
  }
</style></head>
<body>
  <div class="ticket">
    <div class="header bold">${headerLines}</div>
    <div class="divider"></div>
    <div class="center bold sm">
      ${isFiscal ? 'DOCUMENTO AUXILIAR DA<br/>NOTA FISCAL DE CONSUMIDOR<br/>ELETRÔNICA' : 'COMPROVANTE NÃO FISCAL<br/><span class="sm" style="font-weight:normal">Sem valor fiscal</span>'}
      <div class="reimpressao">*** REIMPRESSÃO ***</div>
    </div>
    ${isFiscal ? `
    <div class="divider"></div>
    <div class="center sm">
      NFC-e Nº ${String(sale.nfce_number || '').padStart(9, '0')}<br/>
      SÉRIE: ${safe(sale.nfce_series || '001')}<br/>
      ${safe(dateStr)}
    </div>` : `
    <div class="divider"></div>
    <div class="center sm">${safe(dateStr)}</div>`}

    <div class="divider"></div>
    <div class="bold">ITEM  CÓD  DESCRIÇÃO</div>
    <div class="bold sm">QTD x VL.UNIT  VL.TOTAL</div>
    <div class="divider"></div>
    <div class="items">${itemsHtml || '<div class="sm center">Sem itens</div>'}</div>

    <div class="divider"></div>
    <div class="totals">
      <div class="row"><div>QTD ITENS</div><div>${items.length}</div></div>
      <div class="row"><div>TOTAL</div><div>${brl(totalSale)}</div></div>
      ${sale.discount ? `<div class="row sm"><div>Desconto</div><div>- ${brl(sale.discount)}</div></div>` : ''}
    </div>
    <div class="divider"></div>
    <div class="row sm"><div>Pagamento:</div><div>${safe(paymentMethod).toUpperCase()}</div></div>
    ${customerName ? `<div class="row sm"><div>Cliente:</div><div>${safe(customerName)}</div></div>` : ''}
    ${customerCpf ? `<div class="row sm"><div>CPF/CNPJ:</div><div>${safe(customerCpf)}</div></div>` : ''}

    ${isFiscal ? `
    <div class="divider"></div>
    <div class="center sm bold">Consulte pela chave de acesso em:</div>
    <div class="center sm">www.nfce.fazenda.gov.br/portal</div>
    <div class="key">${safe(sale.nfce_key || '')}</div>
    ${(sale.nfce_qrcode_url || sale.nfce_qr_url || sale.qr_code_url) ? `
    <div class="center" style="margin-top:6px">
      <img alt="QR Code NFC-e" style="width:130px;height:130px;display:block;margin:0 auto;"
        src="https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=0&qzone=1&data=${encodeURIComponent(sale.nfce_qrcode_url || sale.nfce_qr_url || sale.qr_code_url)}"
        onerror="this.replaceWith(Object.assign(document.createElement('div'),{className:'sm', textContent:'(QR Code indisponível)'}))" />
      <div class="sm" style="margin-top:4px">Consumidor: ${safe(customerName) || 'Não identificado'}</div>
    </div>
    ` : ''}
    ${sale.protocolo_autorizacao || sale.nfce_protocol ? `<div class="center sm">Protocolo: ${safe(sale.protocolo_autorizacao || sale.nfce_protocol)}</div>` : ''}
    ` : ''}

    <div class="divider"></div>
    <div class="center sm">Reimpresso em: ${new Date().toLocaleString('pt-BR')}</div>
  </div>

  <script>
    window.addEventListener('load', () => { setTimeout(() => window.print(), 250); });
  </script>
</body></html>`;

      const w = window.open('', '_blank', 'width=400,height=900');
      if (!w) {
        toast.error('Pop-up bloqueado. Permita pop-ups deste site para reimprimir.');
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro ao reimprimir';
      toast.error(`Falha ao reimprimir: ${msg}`);
    }
  };

  const handlePrintSalesReport = async () => {
    try {
      // Buscar dados da empresa para o cabeçalho
      let companyInfo = {};
      try {
        const r = await api.get('/settings/company');
        companyInfo = r?.data || {};
      } catch (_) { /* segue com cabeçalho vazio */ }

      const today = new Date();
      const generatedAt = today.toLocaleString('pt-BR');
      const periodLabel = (() => {
        if (dataInicio && dataFim) {
          return `${new Date(dataInicio).toLocaleDateString('pt-BR')} até ${new Date(dataFim).toLocaleDateString('pt-BR')}`;
        }
        if (dataInicio) return `A partir de ${new Date(dataInicio).toLocaleDateString('pt-BR')}`;
        if (dataFim) return `Até ${new Date(dataFim).toLocaleDateString('pt-BR')}`;
        return 'Todos os períodos';
      })();

      const statusLabel = ({
        'todas': 'Todas',
        'autorizada': 'Autorizadas',
        'pendente': 'Pendentes',
        'nao_enviada': 'Não enviadas',
        'erro': 'Com erro',
        'simulada': 'Simuladas',
        'cancelada': 'Canceladas',
      })[statusFilter] || statusFilter;

      // Subtotais por forma de pagamento
      const byPayment = {};
      filteredSales.forEach(s => {
        const m = getSalePaymentLabel(s);
        const t = getSaleTotal(s);
        byPayment[m] = (byPayment[m] || 0) + t;
      });
      const totalGeral = filteredSales.reduce((sum, s) => sum + getSaleTotal(s), 0);
      const ticketMedio = filteredSales.length ? totalGeral / filteredSales.length : 0;

      const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      const safe = (v) => (v == null ? '' : String(v).replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c])));

      const rows = filteredSales.map((s, i) => {
        const number = s.nfce_number ? `NFC-e ${s.nfce_number}` : (s.id || '').slice(0, 8);
        const customer = s.customer_name || s.cliente_nome || '—';
        const date = s.created_at ? new Date(s.created_at).toLocaleString('pt-BR') : '';
        const payment = getSalePaymentLabel(s);
        const status = s.nfce_status || (s.nfce_key ? 'autorizada' : 'sem nfc-e');
        return `<tr>
          <td>${i + 1}</td>
          <td>${safe(date)}</td>
          <td>${safe(number)}</td>
          <td>${safe(customer)}</td>
          <td>${safe(payment)}</td>
          <td>${safe(status)}</td>
          <td class="num">${brl(getSaleTotal(s))}</td>
        </tr>`;
      }).join('');

      const paymentRows = Object.entries(byPayment).map(([m, v]) => `
        <tr><td>${safe(m).toUpperCase()}</td><td class="num">${brl(v)}</td></tr>
      `).join('');

      const headerLines = [
        (companyInfo.razao_social || companyInfo.nome_fantasia || '').toUpperCase(),
        companyInfo.nome_fantasia && companyInfo.razao_social && companyInfo.nome_fantasia !== companyInfo.razao_social ? companyInfo.nome_fantasia : '',
        [companyInfo.cnpj && `CNPJ: ${companyInfo.cnpj}`, (companyInfo.inscricao_estadual || companyInfo.ie) && `IE: ${companyInfo.inscricao_estadual || companyInfo.ie}`].filter(Boolean).join(' | '),
        [
          companyInfo.endereco || companyInfo.logradouro,
          companyInfo.numero ? `nº ${companyInfo.numero}` : '',
          companyInfo.bairro,
          companyInfo.cidade && (companyInfo.uf ? `${companyInfo.cidade} - ${companyInfo.uf}` : companyInfo.cidade),
          companyInfo.cep ? `CEP ${companyInfo.cep}` : '',
        ].filter(Boolean).join(', '),
        companyInfo.telefone ? `Tel: ${companyInfo.telefone}` : '',
      ].filter(Boolean).map(safe).join('<br/>');

      const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"/>
<title>Relatório de Vendas — ${safe(periodLabel)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #111; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  h2 { font-size: 13px; margin: 12px 0 6px; color: #555; text-transform: uppercase; letter-spacing: .5px; }
  .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 14px; }
  .meta { font-size: 11px; color: #444; margin-top: 4px; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
  .kpi { border: 1px solid #ddd; border-radius: 6px; padding: 8px 10px; }
  .kpi .lbl { font-size: 10px; color: #666; text-transform: uppercase; }
  .kpi .val { font-size: 16px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { border: 1px solid #ddd; padding: 5px 7px; text-align: left; }
  th { background: #f3f3f3; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .totais { margin-top: 16px; }
  .totais table { width: 50%; }
  .footer { margin-top: 18px; font-size: 10px; color: #888; text-align: center; }
  @media print {
    body { margin: 12mm; }
    .no-print { display: none !important; }
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; page-break-after: auto; }
  }
</style></head>
<body>
  <div class="header">
    <h1>${headerLines || 'RELATÓRIO DE VENDAS'}</h1>
    <h2 style="margin-top:8px">Relatório de Vendas</h2>
    <div class="meta">
      <strong>Período:</strong> ${safe(periodLabel)} &nbsp;|&nbsp;
      <strong>Status:</strong> ${safe(statusLabel)} &nbsp;|&nbsp;
      <strong>Gerado em:</strong> ${safe(generatedAt)}
    </div>
  </div>

  <div class="grid">
    <div class="kpi"><div class="lbl">Vendas no filtro</div><div class="val">${filteredSales.length}</div></div>
    <div class="kpi"><div class="lbl">Total</div><div class="val">${brl(totalGeral)}</div></div>
    <div class="kpi"><div class="lbl">Ticket médio</div><div class="val">${brl(ticketMedio)}</div></div>
    <div class="kpi"><div class="lbl">Formas de pagamento</div><div class="val">${Object.keys(byPayment).length}</div></div>
  </div>

  <h2>Vendas</h2>
  <table>
    <thead>
      <tr>
        <th style="width:30px">#</th>
        <th style="width:130px">Data/Hora</th>
        <th style="width:120px">Documento</th>
        <th>Cliente</th>
        <th style="width:110px">Pagamento</th>
        <th style="width:90px">Status</th>
        <th style="width:90px" class="num">Total</th>
      </tr>
    </thead>
    <tbody>${rows || '<tr><td colspan="7" style="text-align:center;color:#888">Nenhuma venda no filtro</td></tr>'}</tbody>
  </table>

  ${paymentRows ? `
  <div class="totais">
    <h2>Subtotais por forma de pagamento</h2>
    <table>
      <thead><tr><th>Forma</th><th class="num">Total</th></tr></thead>
      <tbody>${paymentRows}<tr><td><strong>TOTAL GERAL</strong></td><td class="num"><strong>${brl(totalGeral)}</strong></td></tr></tbody>
    </table>
  </div>` : ''}

  <div class="footer">— Documento gerado pelo sistema. Para fins gerenciais. —</div>

  <script>
    window.addEventListener('load', () => {
      setTimeout(() => { window.print(); }, 250);
    });
  </script>
</body></html>`;

      const w = window.open('', '_blank', 'width=900,height=900');
      if (!w) {
        toast.error('Pop-up bloqueado. Permita pop-ups deste site para imprimir o relatório.');
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (err) {
      toast.error('Erro ao gerar relatório: ' + (err.message || ''));
    }
  };

  const handleReenviarNfce = async (saleId, tipoDoc = 'nfce') => {
    setReenviando(saleId);
    try {
      const response = await salesAPI.reenviarNfce(saleId, tipoDoc);
      const tipoNota = tipoDoc === 'nfe' ? 'NF-e' : 'NFC-e';
      if (response.data.sucesso) {
        if (response.data.numero_ajustado) {
          toast.success(`${tipoNota} ${response.data.nfce_number || response.data.nfe_number} autorizada! (número ajustado por duplicidade)`);
        } else {
          toast.success(`${tipoNota} ${response.data.nfce_number || response.data.nfe_number} autorizada!`);
        }
        loadSales();
      } else {
        toast.error(response.data.mensagem || 'Erro');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro');
    } finally {
      setReenviando(null);
    }
  };

  // Emitir nota fiscal (NFC-e ou NF-e) para qualquer venda
  const handleEmitirNota = async (sale) => {
    if (!sale) return;
    
    setEmitindoNota(true);
    try {
      let endpoint = '';
      
      // Determinar endpoint baseado na fonte
      if (sale.source === 'whatsapp') {
        endpoint = `/api/whatsapp/orders/${sale.id}/emitir-nota`;
      } else {
        endpoint = `/api/sales/${sale.id}/emitir-nota`;
      }
      
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          tipo_documento: tipoDocumento
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.sucesso) {
        const tipoNota = tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e';
        const ambienteLabel = data.ambiente === 'producao' ? 'Produção' : 'Homologação';
        toast.success(`${tipoNota} emitida com sucesso! (${ambienteLabel})`);
        setShowEmitirNotaModal(false);
        setSelectedSale(null);
        loadSales();
      } else {
        toast.error(data.detail || data.mensagem || data.erro || 'Erro ao emitir nota fiscal');
      }
    } catch (error) {
      console.error('Erro ao emitir nota:', error);
      toast.error('Erro ao emitir nota fiscal');
    } finally {
      setEmitindoNota(false);
    }
  };

  const handleViewDetails = async (sale) => {
    setSelectedSale(sale);
    setShowDetailModal(true);
  };

  const handleEditSale = async (saleId) => {
    try {
      const response = await salesAPI.getById(saleId);
      setEditingSale(response.data);
      setShowEditModal(true);
    } catch (error) {
      toast.error('Erro ao carregar venda');
    }
  };

  const handleCleanNonFiscal = async () => {
    if (!window.confirm('Remover vendas não fiscais?')) return;
    try {
      const response = await salesAPI.cleanNonFiscal();
      toast.success(`${response.data.deleted_count} vendas removidas`);
      loadSales();
    } catch (error) {
      toast.error('Erro');
    }
  };

  // Função para obter o valor total de uma venda (adaptável para diferentes formatos)
  const getSaleTotal = (sale) => {
    if (typeof sale.total === 'number') {
      return sale.total;
    }
    if (typeof sale.total === 'string') {
      const match = sale.total.match(/[\d,.]+/);
      return match ? parseFloat(match[0].replace(',', '.')) : 0;
    }
    return 0;
  };

  const getNfceStatusBadge = (sale) => {
    const status = sale.nfce_status || (sale.nfce_key && !sale.nfce_simulado ? 'autorizada' : 'nao_fiscal');
    switch (status) {
      case 'autorizada': return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Autorizada</Badge>;
      case 'cancelada': return <Badge className="bg-slate-200 text-slate-700"><Ban className="w-3 h-3 mr-1" />Cancelada</Badge>;
      case 'simulada': return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" />Simulada</Badge>;
      case 'contingencia': return <Badge className="bg-orange-100 text-orange-700"><AlertTriangle className="w-3 h-3 mr-1" />Contingência</Badge>;
      case 'erro': return <Badge className="bg-rose-100 text-rose-700"><XCircle className="w-3 h-3 mr-1" />Erro</Badge>;
      default: return <Badge variant="outline">Não Fiscal</Badge>;
    }
  };

  const getSourceBadge = (sale) => {
    const source = sale.source || 'pdv';
    const config = sourceIcons[source] || sourceIcons.pdv;
    const Icon = config.icon;
    return (
      <Badge className={`${config.bg} ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const isEditable = (sale) => !sale.nfce_key || sale.nfce_simulado || sale.nfce_status === 'erro' || sale.nfce_status === 'contingencia';
  const canCancel = (sale) => sale.nfce_status === 'autorizada' && !sale.nfce_simulado && sale.nfce_key;
  const canReenviar = (sale) => sale.nfce_simulado || sale.nfce_status === 'erro' || sale.nfce_status === 'contingencia';
  const canEmitir = (sale) => !sale.nfce_key && !sale.nfe_key && sale.nfce_status !== 'autorizada';

  // Filtragem por fonte e status fiscal
  const getEffectiveStatus = (sale) => {
    if (sale.nfce_status === 'autorizada' && !sale.nfce_simulado) return 'autorizada';
    if (sale.nfce_status === 'cancelada') return 'cancelada';
    if (sale.nfce_simulado || sale.nfce_status === 'simulada') return 'simulada';
    if (sale.nfce_status === 'erro') return 'erro';
    if (sale.nfce_status === 'contingencia') return 'contingencia';
    return 'nao_enviada';
  };

  const filteredSales = sales
    // Filtro por fonte
    .filter(s => sourceFilter === 'all' || s.source === sourceFilter)
    // Filtro por status fiscal
    .filter(s => {
      if (statusFilter === 'all') return true;
      const status = getEffectiveStatus(s);
      if (statusFilter === 'nao_enviada') return status === 'nao_enviada' || status === 'erro' || status === 'simulada';
      return status === statusFilter;
    })
    // Ordenar por número da nota (decrescente) e depois por data
    .sort((a, b) => {
      // Primeiro por número da NFC-e (decrescente)
      const numA = a.nfce_number || 0;
      const numB = b.nfce_number || 0;
      if (numA !== numB) return numB - numA;
      // Se não tem número ou igual, ordenar por data (mais recente primeiro)
      const dateA = new Date(a.created_at || a.date || 0);
      const dateB = new Date(b.created_at || b.date || 0);
      return dateB - dateA;
    });

  const totalFiltered = filteredSales.reduce((sum, sale) => sum + getSaleTotal(sale), 0);
  const fiscalSales = filteredSales.filter(s => s.nfce_key && !s.nfce_simulado && s.nfce_status !== 'cancelada').length;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="sales-page">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Relatório de Vendas</h1>
          <p className="text-slate-600">Vendas unificadas - PDV, WhatsApp e Online</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadSales} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button onClick={() => setShowDownloadModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <FileArchive className="w-4 h-4 mr-2" />Baixar XMLs
          </Button>
          <Button onClick={() => setShowInutilizarModal(true)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <Hash className="w-4 h-4 mr-2" />Inutilizar
          </Button>
          <Button onClick={handleCleanNonFiscal} variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50">
            <Trash2 className="w-4 h-4 mr-2" />Limpar
          </Button>
        </div>
      </div>

      {/* Cards de Totais por Fonte */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSourceFilter('all')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
              <Store className="w-4 h-4" /> Total Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-700">{formatCurrency(totals.geral)}</div>
            <p className="text-xs text-slate-500">{sales.length} vendas</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${sourceFilter === 'pdv' ? 'ring-2 ring-blue-500' : ''}`} onClick={() => setSourceFilter('pdv')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-blue-600 flex items-center gap-2">
              <Monitor className="w-4 h-4" /> PDV
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{formatCurrency(totals.pdv)}</div>
            <p className="text-xs text-slate-500">{sales.filter(s => s.source === 'pdv').length} vendas</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${sourceFilter === 'whatsapp' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setSourceFilter('whatsapp')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-green-600 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCurrency(totals.whatsapp)}</div>
            <p className="text-xs text-slate-500">{sales.filter(s => s.source === 'whatsapp').length} vendas</p>
          </CardContent>
        </Card>
        
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${sourceFilter === 'online' ? 'ring-2 ring-purple-500' : ''}`} onClick={() => setSourceFilter('online')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-purple-600 flex items-center gap-2">
              <Globe className="w-4 h-4" /> Online
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{formatCurrency(totals.online)}</div>
            <p className="text-xs text-slate-500">{sales.filter(s => s.source === 'online').length} vendas</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600">Notas Autorizadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{fiscalSales}</div>
            <p className="text-xs text-slate-500">de {filteredSales.length} vendas</p>
          </CardContent>
        </Card>
        
        {/* Card para Notas em Simulação */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Notas em Simulação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => { setShowSimulacaoModal(true); loadVendasSimulacao(); }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Verificar e Reenviar
            </Button>
          </CardContent>
        </Card>

        {/* Card para Limpar Duplicados */}
        <Card className="border-purple-200 bg-purple-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-purple-700 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Vendas Duplicadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
              onClick={() => { setShowDuplicadosModal(true); loadDuplicados(); }}
            >
              <Search className="w-4 h-4 mr-2" />
              Verificar e Limpar
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Filtro por Status Fiscal */}
      <div className="mb-6">
        <Label className="text-sm text-slate-600 mb-2 block">Filtrar por Status Fiscal:</Label>
        <div className="flex flex-wrap gap-2">
          <Button 
            size="sm" 
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
          >
            Todos ({sales.length})
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'autorizada' ? 'default' : 'outline'}
            className={statusFilter === 'autorizada' ? 'bg-emerald-600' : 'border-emerald-300 text-emerald-700'}
            onClick={() => setStatusFilter('autorizada')}
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            Autorizadas ({sales.filter(s => getEffectiveStatus(s) === 'autorizada').length})
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'nao_enviada' ? 'default' : 'outline'}
            className={statusFilter === 'nao_enviada' ? 'bg-rose-600' : 'border-rose-300 text-rose-700'}
            onClick={() => setStatusFilter('nao_enviada')}
          >
            <XCircle className="w-3 h-3 mr-1" />
            Não Enviadas ({sales.filter(s => ['nao_enviada', 'erro', 'simulada'].includes(getEffectiveStatus(s))).length})
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'erro' ? 'default' : 'outline'}
            className={statusFilter === 'erro' ? 'bg-red-600' : 'border-red-300 text-red-700'}
            onClick={() => setStatusFilter('erro')}
          >
            <XCircle className="w-3 h-3 mr-1" />
            Com Erro ({sales.filter(s => getEffectiveStatus(s) === 'erro').length})
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'simulada' ? 'default' : 'outline'}
            className={statusFilter === 'simulada' ? 'bg-amber-600' : 'border-amber-300 text-amber-700'}
            onClick={() => setStatusFilter('simulada')}
          >
            <AlertTriangle className="w-3 h-3 mr-1" />
            Simuladas ({sales.filter(s => getEffectiveStatus(s) === 'simulada').length})
          </Button>
          <Button 
            size="sm" 
            variant={statusFilter === 'cancelada' ? 'default' : 'outline'}
            className={statusFilter === 'cancelada' ? 'bg-slate-600' : 'border-slate-300 text-slate-700'}
            onClick={() => setStatusFilter('cancelada')}
          >
            <Ban className="w-3 h-3 mr-1" />
            Canceladas ({sales.filter(s => getEffectiveStatus(s) === 'cancelada').length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            onClick={() => handlePrintSalesReport()}
            data-testid="print-sales-report-btn"
            title="Imprimir relatório de vendas com os filtros atuais"
          >
            <Printer className="w-3 h-3 mr-1" />
            Imprimir Relatório
          </Button>
        </div>
      </div>

      {/* Card de Diagnóstico NFC-e */}
      {diagnostico && !diagnostico.pronto_para_emitir && (
        <Card className="mb-6 border-rose-200 bg-rose-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-rose-700 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Diagnóstico NFC-e - Configurações Pendentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className={`p-2 rounded text-center ${diagnostico.certificado.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`}>
                <p className="text-xs font-medium">Certificado</p>
                <p className="text-sm font-bold">{diagnostico.certificado.status}</p>
              </div>
              <div className={`p-2 rounded text-center ${diagnostico.csc.status === 'OK' ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`}>
                <p className="text-xs font-medium">CSC</p>
                <p className="text-sm font-bold">{diagnostico.csc.status}</p>
              </div>
              <div className="p-2 rounded text-center bg-blue-100 text-blue-700">
                <p className="text-xs font-medium">Ambiente</p>
                <p className="text-sm font-bold">{diagnostico.ambiente.nfce === 'producao' ? 'Produção' : 'Homologação'}</p>
              </div>
              <div className={`p-2 rounded text-center ${diagnostico.empresa.cnpj !== 'NÃO CONFIGURADO' ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`}>
                <p className="text-xs font-medium">Empresa</p>
                <p className="text-sm font-bold">{diagnostico.empresa.cnpj !== 'NÃO CONFIGURADO' ? 'OK' : 'PENDENTE'}</p>
              </div>
            </div>
            {diagnostico.problemas.length > 0 && (
              <div className="bg-white p-3 rounded border border-rose-200">
                <p className="text-xs font-semibold text-rose-700 mb-2">Problemas encontrados:</p>
                <ul className="text-xs text-rose-600 space-y-1">
                  {diagnostico.problemas.map((p, i) => (
                    <li key={i}>• {p}</li>
                  ))}
                </ul>
              </div>
            )}
            <Button 
              size="sm" 
              className="mt-3 bg-rose-600 hover:bg-rose-700"
              onClick={() => window.location.href = '/configuracoes'}
            >
              Ir para Configurações
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filtros por Fonte */}
      <div className="flex gap-2 mb-4">
        <Button 
          variant={sourceFilter === 'all' ? 'default' : 'outline'} 
          size="sm" 
          onClick={() => setSourceFilter('all')}
        >
          Todos
        </Button>
        <Button 
          variant={sourceFilter === 'pdv' ? 'default' : 'outline'} 
          size="sm" 
          onClick={() => setSourceFilter('pdv')}
          className={sourceFilter === 'pdv' ? 'bg-blue-600' : ''}
        >
          <Monitor className="w-4 h-4 mr-1" /> PDV
        </Button>
        <Button 
          variant={sourceFilter === 'whatsapp' ? 'default' : 'outline'} 
          size="sm" 
          onClick={() => setSourceFilter('whatsapp')}
          className={sourceFilter === 'whatsapp' ? 'bg-green-600' : ''}
        >
          <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
        </Button>
        <Button 
          variant={sourceFilter === 'online' ? 'default' : 'outline'} 
          size="sm" 
          onClick={() => setSourceFilter('online')}
          className={sourceFilter === 'online' ? 'bg-purple-600' : ''}
        >
          <Globe className="w-4 h-4 mr-1" /> Online
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Origem</TableHead>
              <TableHead>Data</TableHead>
              <TableHead>Série</TableHead>
              <TableHead>Número</TableHead>
              <TableHead>Status Fiscal</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead>Total</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  Carregando vendas...
                </TableCell>
              </TableRow>
            ) : filteredSales.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                  Nenhuma venda encontrada
                </TableCell>
              </TableRow>
            ) : (
              filteredSales.map(sale => (
                <TableRow key={`${sale.source}-${sale.id}`} data-testid={`sale-row-${sale.id}`}>
                  <TableCell>{getSourceBadge(sale)}</TableCell>
                  <TableCell className="text-sm">{formatDate(sale.created_at)}</TableCell>
                  <TableCell><span className="font-mono font-semibold">{sale.nfce_series || 1}</span></TableCell>
                  <TableCell><span className="font-mono font-semibold">{sale.nfce_number || '-'}</span></TableCell>
                  <TableCell>{getNfceStatusBadge(sale)}</TableCell>
                  <TableCell className="text-sm">{sale.customer_name || 'Sem cadastro'}</TableCell>
                  <TableCell className="font-semibold">{formatCurrency(getSaleTotal(sale))}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {/* Botão Emitir Nota - apenas se não tiver nota */}
                      {canEmitir(sale) && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => {
                              setSelectedSale(sale);
                              setTipoDocumento('nfce');
                              setShowEmitirNotaModal(true);
                            }} 
                            className="text-green-600 border-green-300 hover:bg-green-50"
                            title="Emitir NFC-e"
                          >
                            <Receipt className="w-4 h-4" />
                            <span className="hidden sm:inline ml-1">NFC-e</span>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => {
                              setSelectedSale(sale);
                              setTipoDocumento('nfe');
                              setShowEmitirNotaModal(true);
                            }} 
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            title="Emitir NF-e"
                          >
                            <FileText className="w-4 h-4" />
                            <span className="hidden sm:inline ml-1">NF-e</span>
                          </Button>
                        </>
                      )}
                      
                      {/* Botão Editar - apenas para vendas PDV editáveis */}
                      {sale.source === 'pdv' && isEditable(sale) && (
                        <Button size="sm" variant="outline" onClick={() => handleEditSale(sale.id)} className="text-blue-600 border-blue-300">
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Botão Reenviar */}
                      {sale.source === 'pdv' && canReenviar(sale) && (
                        <Button size="sm" variant="outline" onClick={() => handleReenviarNfce(sale.id)} disabled={reenviando === sale.id} className="text-amber-600 border-amber-300">
                          {reenviando === sale.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </Button>
                      )}
                      
                      {/* Botão Cancelar */}
                      {sale.source === 'pdv' && canCancel(sale) && (
                        <Button size="sm" variant="outline" onClick={() => { setCancelingSale(sale); setShowCancelModal(true); }} className="text-rose-600 border-rose-300">
                          <Ban className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Botão Download XML */}
                      {sale.nfce_key && !sale.nfce_simulado && (
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadXml(sale.id)} title="Baixar XML" data-testid={`download-xml-${sale.id}`}>
                          <Download className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                      
                      {/* Botão Reimprimir Cupom */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleReprintCupom(sale.id)}
                        title="Reimprimir cupom"
                        data-testid={`reprint-${sale.id}`}
                        className="text-violet-600"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      
                      {/* Botão Ver Detalhes */}
                      <Button size="sm" variant="ghost" onClick={() => handleViewDetails(sale)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal de Detalhes */}
      <SimpleModal open={showDetailModal} onClose={() => setShowDetailModal(false)} title="Detalhes da Venda">
        {selectedSale && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              {getSourceBadge(selectedSale)}
              {getNfceStatusBadge(selectedSale)}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>Número: <strong>{selectedSale.nfce_number || '-'}</strong></div>
              <div>Total: <strong>{formatCurrency(getSaleTotal(selectedSale))}</strong></div>
            </div>
            <div className="text-xs font-mono break-all">Chave: {selectedSale.nfce_key || '-'}</div>
            <Separator />
            <div className="font-semibold">Itens:</div>
            {selectedSale.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm bg-slate-50 p-2 rounded">
                <span>{item.product_name || item.name} x{item.quantity}</span>
                <span>{formatCurrency(item.total || (item.quantity * (item.unit_price || item.price || 0)))}</span>
              </div>
            )) || <p className="text-sm text-slate-500">Sem itens detalhados</p>}
          </div>
        )}
      </SimpleModal>

      {/* Modal de Edição */}
      <EditSaleModal
        sale={editingSale}
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingSale(null); }}
        onSaved={loadSales}
      />

      {/* Modal Emitir Nota Fiscal */}
      <SimpleModal 
        open={showEmitirNotaModal} 
        onClose={() => { setShowEmitirNotaModal(false); setSelectedSale(null); }} 
        title="Emitir Documento Fiscal"
      >
        {selectedSale && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              {getSourceBadge(selectedSale)}
              <span className="text-sm text-slate-500">ID: {selectedSale.id?.substring(0, 8).toUpperCase()}</span>
            </div>
            <p className="text-lg font-semibold">Total: {formatCurrency(getSaleTotal(selectedSale))}</p>
            
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setTipoDocumento('nfce')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  tipoDocumento === 'nfce' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className="font-semibold block">NFC-e - Nota Fiscal do Consumidor</span>
                <span className="text-sm text-slate-500">Para vendas a consumidor final (pessoa física)</span>
              </button>
              <button
                type="button"
                onClick={() => setTipoDocumento('nfe')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  tipoDocumento === 'nfe' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <span className="font-semibold block">NF-e - Nota Fiscal Eletrônica</span>
                <span className="text-sm text-slate-500">Para vendas entre empresas (B2B)</span>
              </button>
            </div>
            
            <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
              O ambiente de emissão (Homologação/Produção) é definido nas Configurações Fiscais.
            </p>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => { setShowEmitirNotaModal(false); setSelectedSale(null); }} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={() => handleEmitirNota(selectedSale)} 
                disabled={emitindoNota}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {emitindoNota ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                {emitindoNota ? 'Emitindo...' : `Emitir ${tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e'}`}
              </Button>
            </div>
          </div>
        )}
      </SimpleModal>

      {/* Modal Download */}
      <SimpleModal open={showDownloadModal} onClose={() => setShowDownloadModal(false)} title="Download de XMLs">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Selecione o período e tipo de documento para baixar os XMLs.</p>
          
          {/* Tipo de Documento */}
          <div>
            <Label className="mb-2 block">Tipo de Documento</Label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setTipoDocumentoDownload('nfce')}
                className={`p-3 rounded-lg border-2 transition-all text-center ${
                  tipoDocumentoDownload === 'nfce' 
                    ? 'border-green-500 bg-green-50 text-green-700' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <Receipt className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm font-medium">NFC-e</span>
              </button>
              <button
                type="button"
                onClick={() => setTipoDocumentoDownload('nfe')}
                className={`p-3 rounded-lg border-2 transition-all text-center ${
                  tipoDocumentoDownload === 'nfe' 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FileText className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm font-medium">NF-e</span>
              </button>
              <button
                type="button"
                onClick={() => setTipoDocumentoDownload('todos')}
                className={`p-3 rounded-lg border-2 transition-all text-center ${
                  tipoDocumentoDownload === 'todos' 
                    ? 'border-purple-500 bg-purple-50 text-purple-700' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <FileArchive className="w-5 h-5 mx-auto mb-1" />
                <span className="text-sm font-medium">Ambos</span>
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data Início</Label>
              <Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div>
              <Label>Data Fim</Label>
              <Input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </div>
          <Button onClick={handleCheckXmlCount} variant="outline" className="w-full">
            <Calendar className="w-4 h-4 mr-2" />Verificar XMLs
          </Button>
          {xmlCount > 0 && (
            <div className="bg-emerald-50 p-3 rounded-lg text-center">
              <p className="text-emerald-700 font-semibold">{xmlCount} XML(s) encontrado(s)</p>
            </div>
          )}
          <Button onClick={handleDownloadBatch} className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={!dataInicio || !dataFim}>
            <FileArchive className="w-4 h-4 mr-2" />Baixar ZIP
          </Button>
        </div>
      </SimpleModal>

      {/* Modal Cancelar */}
      <SimpleModal open={showCancelModal} onClose={() => { setShowCancelModal(false); setCancelingSale(null); setCancelJustificativa(''); }} title="Cancelar NFC-e">
        {cancelingSale && (
          <div className="space-y-4">
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <p className="text-sm text-rose-700"><AlertTriangle className="w-4 h-4 inline mr-1" />Esta ação não pode ser desfeita.</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-sm">
              <div>Série: <strong>{cancelingSale.nfce_series || 1}</strong> | Número: <strong>{cancelingSale.nfce_number}</strong></div>
              <div>Valor: <strong>{formatCurrency(getSaleTotal(cancelingSale))}</strong></div>
            </div>
            <div>
              <Label>Justificativa *</Label>
              <textarea 
                className="w-full mt-1 p-2 border rounded-md text-sm" 
                rows={3}
                placeholder="Mínimo 15 caracteres"
                value={cancelJustificativa}
                onChange={(e) => setCancelJustificativa(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">{cancelJustificativa.length}/15 caracteres</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">Prazo: até 24 horas após emissão.</p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowCancelModal(false); setCancelingSale(null); }}>Voltar</Button>
              <Button onClick={handleCancelarNfce} disabled={loading || cancelJustificativa.length < 15} className="bg-rose-600 hover:bg-rose-700">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}Confirmar
              </Button>
            </div>
          </div>
        )}
      </SimpleModal>

      {/* Modal Inutilizar */}
      <SimpleModal open={showInutilizarModal} onClose={() => { setShowInutilizarModal(false); setInutNumeroInicial(''); setInutNumeroFinal(''); setInutJustificativa(''); }} title="Inutilizar Numeração">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Números já usados não podem ser inutilizados.</p>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Série</Label>
              <Input type="number" min="1" value={inutSerie} onChange={(e) => setInutSerie(e.target.value)} />
            </div>
            <div>
              <Label>Nº Inicial</Label>
              <Input type="number" min="1" value={inutNumeroInicial} onChange={(e) => setInutNumeroInicial(e.target.value)} />
            </div>
            <div>
              <Label>Nº Final</Label>
              <Input type="number" min="1" value={inutNumeroFinal} onChange={(e) => setInutNumeroFinal(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Justificativa *</Label>
            <textarea 
              className="w-full mt-1 p-2 border rounded-md text-sm" 
              rows={3}
              placeholder="Mínimo 15 caracteres"
              value={inutJustificativa}
              onChange={(e) => setInutJustificativa(e.target.value)}
            />
            <p className="text-xs text-slate-500 mt-1">{inutJustificativa.length}/15 caracteres</p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowInutilizarModal(false)}>Cancelar</Button>
            <Button onClick={handleInutilizar} disabled={loading || inutJustificativa.length < 15 || !inutNumeroInicial || !inutNumeroFinal} className="bg-amber-600 hover:bg-amber-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Hash className="w-4 h-4 mr-2" />}Inutilizar
            </Button>
          </div>
        </div>
      </SimpleModal>

      {/* Modal de Notas em Simulação */}
      <SimpleModal 
        open={showSimulacaoModal} 
        onClose={() => setShowSimulacaoModal(false)} 
        title="Notas em Simulação - Reenviar para SEFAZ"
      >
        <div className="space-y-4">
          {/* Botão de Diagnóstico */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-blue-800">Verificar Configurações</h4>
                <p className="text-sm text-blue-600">Clique para verificar se as configurações estão corretas antes de reenviar</p>
              </div>
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    const response = await fetch(`${BACKEND_URL}/api/nfce/status-sefaz`, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                    });
                    const data = await response.json();
                    if (response.ok) {
                      const config = data.configuracao;
                      if (config.pode_emitir) {
                        toast.success('✅ Configurações OK! Pode reenviar as notas.');
                      } else {
                        let problemas = [];
                        if (config.certificado.status !== 'OK') problemas.push('Certificado/Senha');
                        if (config.csc.status !== 'OK') problemas.push('CSC');
                        if (config.empresa.status !== 'OK') problemas.push('Dados da Empresa');
                        toast.error(`❌ Configure primeiro: ${problemas.join(', ')}`);
                      }
                    }
                  } catch (error) {
                    toast.error('Erro ao verificar configurações');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Verificar
              </Button>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Atenção:</strong> Estas vendas foram emitidas em modo de simulação (sem valor fiscal).
              Após configurar corretamente o certificado digital e CSC, você pode reenviá-las para a SEFAZ.
            </p>
          </div>
          
          {loadingSimulacao ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : vendasSimulacao.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-green-700 font-semibold">Nenhuma nota em simulação!</p>
              <p className="text-sm text-slate-500">Todas as notas foram emitidas corretamente.</p>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">
                  {vendasSimulacao.length} nota(s) em simulação
                </span>
                <div className="flex gap-2">
                  <Button
                    onClick={handleReenviarLote}
                    disabled={reenviadoLote}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    {reenviadoLote ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reenviar Todas
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Nº</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vendasSimulacao.map((venda) => (
                      <TableRow key={venda.id}>
                        <TableCell className="text-sm">
                          {new Date(venda.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-sm font-mono">
                          {venda.nfce_number || '-'}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(venda.total)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                            Simulação
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReenviarSimulacao(venda.id)}
                              disabled={reenviando === venda.id}
                              className="border-amber-300 text-amber-700 hover:bg-amber-50"
                              title="Reenviar para SEFAZ"
                            >
                              {reenviando === venda.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                if (!window.confirm('Tem certeza que deseja excluir esta venda em simulação? Esta ação não pode ser desfeita.')) return;
                                try {
                                  const response = await fetch(`${BACKEND_URL}/api/sales/${venda.id}`, {
                                    method: 'DELETE',
                                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                  });
                                  if (response.ok) {
                                    toast.success('Venda excluída');
                                    loadVendasSimulacao();
                                    loadSales();
                                  } else {
                                    toast.error('Erro ao excluir');
                                  }
                                } catch (error) {
                                  toast.error('Erro ao excluir');
                                }
                              }}
                              className="border-red-300 text-red-700 hover:bg-red-50"
                              title="Excluir venda"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
          
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-800 mb-2">Para reenviar com sucesso:</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>✓ Certificado digital A1 configurado com senha</li>
              <li>✓ CSC (Código de Segurança) e ID do CSC preenchidos</li>
              <li>✓ Dados da empresa completos (CNPJ, UF, Código IBGE)</li>
              <li>✓ Ambiente configurado como "Produção" para notas reais</li>
            </ul>
          </div>
        </div>
      </SimpleModal>

      {/* Modal de Vendas Duplicadas */}
      <SimpleModal 
        open={showDuplicadosModal} 
        onClose={() => setShowDuplicadosModal(false)} 
        title="Vendas com Números Duplicados"
      >
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800">
              <strong>O que são duplicados?</strong> São vendas que possuem o mesmo número de NFC-e. 
              Isso pode acontecer quando uma venda é reenviada ou convertida para fiscal várias vezes.
            </p>
          </div>

          {!duplicados ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" />
              <p className="text-slate-500 mt-2">Buscando duplicados...</p>
            </div>
          ) : duplicados.total_numeros_duplicados === 0 ? (
            <div className="text-center py-8 bg-emerald-50 rounded-lg">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
              <p className="text-emerald-700 font-semibold mt-2">Nenhuma duplicata encontrada!</p>
              <p className="text-sm text-emerald-600">Todas as vendas possuem números únicos.</p>
            </div>
          ) : (
            <>
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-rose-800">
                      {duplicados.total_numeros_duplicados} número(s) com duplicatas
                    </p>
                    <p className="text-sm text-rose-600">
                      Total de {duplicados.total_vendas_para_excluir} venda(s) que podem ser excluídas
                    </p>
                  </div>
                  <Button
                    onClick={handleLimparDuplicados}
                    disabled={limpandoDuplicados}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    {limpandoDuplicados ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Limpar Todos
                  </Button>
                </div>
              </div>

              <div className="max-h-80 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nº NFC-e</TableHead>
                      <TableHead>Quantidade</TableHead>
                      <TableHead>Manter</TableHead>
                      <TableHead>Excluir</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(duplicados.duplicados).map(([numero, info]) => (
                      <TableRow key={numero}>
                        <TableCell className="font-bold">{numero}</TableCell>
                        <TableCell>{info.quantidade} vendas</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-1 rounded ${
                            info.manter.nfce_status === 'autorizada' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {info.manter.nfce_status === 'autorizada' ? 'Autorizada' : 'Mais recente'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded">
                            {info.excluir.length} venda(s)
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-800 mb-2">Como funciona a limpeza:</h4>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>✓ Se existe uma venda <strong>autorizada</strong>, ela será mantida</li>
              <li>✓ Se nenhuma está autorizada, mantém a <strong>mais recente</strong></li>
              <li>✓ As outras vendas com mesmo número são excluídas</li>
            </ul>
          </div>
        </div>
      </SimpleModal>
    </div>
  );
};
